/**
 * RevolutionImageZoom
 *
 * Product fullscreen gallery — vertical stack of images and videos.
 *
 * Image gestures:
 * - Pinch from 1x to 4x
 * - One-finger pan while zoomed
 * - Double tap/click to toggle 2.5x zoom
 *
 * CSS state:
 * - `.pswp--zoomed` on the gallery root
 * - `.is-active` on `.pswp__zoom-layer`
 *
 * Loaded from `sections/main-product.liquid`.
 */

import { Delegate } from '@theme/delegate';
import { responsive } from '@theme/responsive';
import { bodyScrollLockOptions } from '@theme/body-scroll-lock-options';
import { disableBodyScroll, enableBodyScroll } from '@theme/body-scroll-lock';

const MIN_ZOOM = 1;
const MAX_ZOOM = 4;
const DOUBLE_TAP_ZOOM = 2.5;
const ZOOM_RESET_THRESHOLD = 1.02;
const DOUBLE_TAP_DELAY = 300;
const DOUBLE_TAP_DISTANCE = 24;
const TAP_MOVE_DISTANCE = 10;

const clamp = (value, min, max) => Math.min(Math.max(value, min), max);

const getTouchDistance = (touches) =>
  Math.hypot(touches[0].clientX - touches[1].clientX, touches[0].clientY - touches[1].clientY);

const getTouchCenter = (touches) => ({
  x: (touches[0].clientX + touches[1].clientX) / 2,
  y: (touches[0].clientY + touches[1].clientY) / 2,
});

const createZoomState = () => ({
  active: false,
  source: null,
  mode: null,
  scale: MIN_ZOOM,
  x: 0,
  y: 0,
  baseWidth: 0,
  baseHeight: 0,
  startScale: MIN_ZOOM,
  startDistance: 0,
  startX: 0,
  startY: 0,
  startTouchX: 0,
  startTouchY: 0,
  focalX: 0,
  focalY: 0,
  moved: false,
  tapImage: null,
});

class RevolutionImageZoom {
  constructor(element, slideshow) {
    this.element = element;
    this.slideshow = slideshow;
    this.delegateElement = new Delegate(this.element);
    this.delegateRoot = new Delegate(document.body);
    this.pswpElement = document.querySelector('.pswp');
    this.scrollWrap = this.pswpElement?.querySelector('.pswp__scroll-wrap');
    this.stackElement = this.pswpElement?.querySelector('.pswp__stack');
    this.zoomLayer = this.pswpElement?.querySelector('.pswp__zoom-layer');
    this.zoomImage = this.zoomLayer?.querySelector('.pswp__zoom-image');
    this.zoomState = createZoomState();
    this.lastTap = null;
    this.ignoreDblClickUntil = 0;
    this.isOpen = false;

    this.onKeydown = this.onKeydown.bind(this);
    this.onTouchStart = this.onTouchStart.bind(this);
    this.onTouchMove = this.onTouchMove.bind(this);
    this.onTouchEnd = this.onTouchEnd.bind(this);
    this.onTouchCancel = this.onTouchCancel.bind(this);
    this.onDoubleClick = this.onDoubleClick.bind(this);
    this.bindEvents();
  }

  bindEvents() {
    this.delegateElement.on(
      'click',
      '.main-product__slide--zoom',
      this.openGalleryFromImageClick.bind(this)
    );
    this.delegateRoot.on('click', '.pswp__button--close', this.closeGallery.bind(this));
    this.delegateRoot.on('click', '.pswp__bg', this.closeGallery.bind(this));

    this.pswpElement?.addEventListener('touchstart', this.onTouchStart, { passive: false });
    this.pswpElement?.addEventListener('touchmove', this.onTouchMove, { passive: false });
    this.pswpElement?.addEventListener('touchend', this.onTouchEnd, { passive: false });
    this.pswpElement?.addEventListener('touchcancel', this.onTouchCancel);
    this.pswpElement?.addEventListener('dblclick', this.onDoubleClick);
  }

  destroy() {
    this.closeGallery();
    this.delegateElement.off();
    this.delegateRoot.off();
    this.pswpElement?.removeEventListener('touchstart', this.onTouchStart);
    this.pswpElement?.removeEventListener('touchmove', this.onTouchMove);
    this.pswpElement?.removeEventListener('touchend', this.onTouchEnd);
    this.pswpElement?.removeEventListener('touchcancel', this.onTouchCancel);
    this.pswpElement?.removeEventListener('dblclick', this.onDoubleClick);
  }

  getActiveGallery() {
    return responsive.matchesBreakpoint('pocket')
      ? this.element.querySelector('.main-product__gallery--mobile')
      : this.element.querySelector('.main-product__gallery--desktop');
  }

  // All zoomable slides (images + videos) in DOM = media order. Model slides are
  // excluded because they don't carry the `--zoom` class.
  getGallerySlides() {
    const gallery = this.getActiveGallery();
    if (!gallery) return [];

    return Array.from(gallery.querySelectorAll('.main-product__slide--zoom'));
  }

  openGalleryFromImageClick(event, target) {
    // A click on the video control bar plays/seeks/mutes — it must not open the lightbox.
    if (event.target.closest?.('.section-video__controls')) return;

    // Resolve slides from the gallery that actually contains the clicked slide, so the
    // opened index is correct regardless of breakpoint / which gallery is active.
    const gallery = target.closest('.main-product__gallery') || this.getActiveGallery();
    const slides = gallery
      ? Array.from(gallery.querySelectorAll('.main-product__slide--zoom'))
      : this.getGallerySlides();
    const index = slides.indexOf(target);

    this.openGallery(slides, index < 0 ? 0 : index);
  }

  // Build a stack item for the fullscreen lightbox: a full-res <img> for image slides,
  // or a fresh <video-component> (our video.js + custom controls) for video / external slides.
  buildStackItem(slide) {
    const item = document.createElement('div');
    item.className = 'pswp__stack-item';

    const inlineVideo = slide.querySelector('video-component');
    if (inlineVideo) {
      // Reuse the inline settings + template so the enlarged video keeps OUR custom controls.
      const ratioSource = slide.querySelector('.main-product__video-ratio');
      const ratio = document.createElement('div');
      ratio.className = 'main-product__video-ratio';
      const ratioStyle = ratioSource?.getAttribute('style');
      if (ratioStyle) ratio.setAttribute('style', ratioStyle);

      const clone = document.createElement('video-component');
      clone.className = 'main-product__video';
      clone.setAttribute(
        'data-section-settings',
        inlineVideo.getAttribute('data-section-settings') || '{}'
      );

      const template = inlineVideo.querySelector('template');
      if (template) {
        const templateClone = template.cloneNode(true);
        // Keep <video> ids unique so the enlarged player can't collide with the inline one.
        const videoEl = templateClone.content?.querySelector('video');
        if (videoEl?.id) videoEl.id = `${videoEl.id}-lightbox`;
        clone.appendChild(templateClone);
      }

      ratio.appendChild(clone);
      item.appendChild(ratio);
      return item;
    }

    const source = slide.querySelector('img');
    const img = document.createElement('img');
    if (source) {
      img.src = source.dataset.originalSrc || source.currentSrc || source.src;
      img.alt = source.alt || '';
      img.width = parseInt(source.getAttribute('data-max-width'), 10) || undefined;
      img.height = parseInt(source.getAttribute('data-max-height'), 10) || undefined;
    }
    img.loading = 'eager';
    img.decoding = 'async';
    item.appendChild(img);
    return item;
  }

  getStackImage(target) {
    const item = target?.closest?.('.pswp__stack-item');
    if (!item || item.querySelector('.main-product__video-ratio')) return null;

    return Array.from(item.children).find((child) => child.matches('img')) ?? null;
  }

  activateImageZoom(source, scale = MIN_ZOOM, clientX, clientY) {
    if (!source || !this.zoomLayer || !this.zoomImage) return false;

    const src = source.dataset.originalSrc || source.currentSrc || source.src;
    if (!src) return false;

    this.zoomState = {
      ...createZoomState(),
      active: true,
      source,
    };

    this.zoomImage.src = src;
    this.zoomImage.alt = source.alt || '';

    ['width', 'height'].forEach((attribute) => {
      const value = source.getAttribute(attribute);
      if (value) {
        this.zoomImage.setAttribute(attribute, value);
      } else {
        this.zoomImage.removeAttribute(attribute);
      }
    });

    this.zoomLayer.classList.add('is-active');
    this.zoomLayer.setAttribute('aria-hidden', 'false');
    this.pswpElement.classList.add('pswp--zoomed');

    this.zoomState.baseWidth = this.zoomImage.offsetWidth;
    this.zoomState.baseHeight = this.zoomImage.offsetHeight;
    this.updateZoomTransform();

    if (scale > MIN_ZOOM) {
      const layerRect = this.zoomLayer.getBoundingClientRect();
      const pointX = clientX ?? layerRect.left + layerRect.width / 2;
      const pointY = clientY ?? layerRect.top + layerRect.height / 2;
      this.zoomAroundPoint(scale, pointX, pointY);
    }

    return true;
  }

  resetImageZoom() {
    this.zoomLayer?.classList.remove('is-active');
    this.zoomLayer?.setAttribute('aria-hidden', 'true');
    this.pswpElement?.classList.remove('pswp--zoomed');

    if (this.zoomImage) {
      this.zoomImage.style.removeProperty('transform');
      this.zoomImage.removeAttribute('src');
      this.zoomImage.width = 1;
      this.zoomImage.height = 1;
      this.zoomImage.alt = '';
    }

    this.zoomState = createZoomState();
    this.lastTap = null;
  }

  zoomAroundPoint(scale, clientX, clientY) {
    const layerRect = this.zoomLayer.getBoundingClientRect();
    const relativeX = clientX - (layerRect.left + layerRect.width / 2);
    const relativeY = clientY - (layerRect.top + layerRect.height / 2);
    const focalX = (relativeX - this.zoomState.x) / this.zoomState.scale;
    const focalY = (relativeY - this.zoomState.y) / this.zoomState.scale;

    this.zoomState.scale = clamp(scale, MIN_ZOOM, MAX_ZOOM);
    this.zoomState.x = relativeX - focalX * this.zoomState.scale;
    this.zoomState.y = relativeY - focalY * this.zoomState.scale;
    this.clampZoomPan();
    this.updateZoomTransform();
  }

  clampZoomPan() {
    if (!this.zoomLayer) return;

    const maxX = Math.max(
      (this.zoomState.baseWidth * this.zoomState.scale - this.zoomLayer.clientWidth) / 2,
      0
    );
    const maxY = Math.max(
      (this.zoomState.baseHeight * this.zoomState.scale - this.zoomLayer.clientHeight) / 2,
      0
    );

    this.zoomState.x = clamp(this.zoomState.x, -maxX, maxX);
    this.zoomState.y = clamp(this.zoomState.y, -maxY, maxY);
  }

  updateZoomTransform() {
    if (!this.zoomImage) return;

    this.zoomImage.style.transform = `translate3d(${this.zoomState.x}px, ${this.zoomState.y}px, 0) scale(${this.zoomState.scale})`;
  }

  startPinch(event) {
    if (event.touches.length < 2) return;

    const source = this.zoomState.active ? this.zoomState.source : this.getStackImage(event.target);
    if (!source || (!this.zoomState.active && !this.activateImageZoom(source))) return;

    if (event.cancelable) event.preventDefault();

    const center = getTouchCenter(event.touches);
    const layerRect = this.zoomLayer.getBoundingClientRect();
    const relativeX = center.x - (layerRect.left + layerRect.width / 2);
    const relativeY = center.y - (layerRect.top + layerRect.height / 2);

    this.zoomState.mode = 'pinch';
    this.zoomState.startDistance = Math.max(getTouchDistance(event.touches), 1);
    this.zoomState.startScale = this.zoomState.scale;
    this.zoomState.focalX = (relativeX - this.zoomState.x) / this.zoomState.scale;
    this.zoomState.focalY = (relativeY - this.zoomState.y) / this.zoomState.scale;
    this.zoomState.moved = false;
  }

  startPan(touch) {
    this.zoomState.mode = 'pan';
    this.zoomState.startTouchX = touch.clientX;
    this.zoomState.startTouchY = touch.clientY;
    this.zoomState.startX = this.zoomState.x;
    this.zoomState.startY = this.zoomState.y;
    this.zoomState.moved = false;
  }

  onTouchStart(event) {
    if (!this.isOpen || event.target.closest?.('.pswp__button--close')) return;

    if (event.touches.length >= 2) {
      this.startPinch(event);
      return;
    }

    const touch = event.touches[0];
    if (!touch) return;

    if (this.zoomState.active) {
      if (event.cancelable) event.preventDefault();
      this.startPan(touch);
      return;
    }

    const source = this.getStackImage(event.target);
    if (!source) return;

    this.zoomState.mode = 'tap';
    this.zoomState.tapImage = source;
    this.zoomState.startTouchX = touch.clientX;
    this.zoomState.startTouchY = touch.clientY;
    this.zoomState.moved = false;
  }

  onTouchMove(event) {
    if (event.touches.length >= 2) {
      if (this.zoomState.mode !== 'pinch') this.startPinch(event);
      if (!this.zoomState.active || this.zoomState.mode !== 'pinch') return;

      if (event.cancelable) event.preventDefault();

      const center = getTouchCenter(event.touches);
      const layerRect = this.zoomLayer.getBoundingClientRect();
      const relativeX = center.x - (layerRect.left + layerRect.width / 2);
      const relativeY = center.y - (layerRect.top + layerRect.height / 2);
      const scale = clamp(
        (getTouchDistance(event.touches) / this.zoomState.startDistance) *
          this.zoomState.startScale,
        MIN_ZOOM,
        MAX_ZOOM
      );

      this.zoomState.scale = scale;
      this.zoomState.x = relativeX - this.zoomState.focalX * scale;
      this.zoomState.y = relativeY - this.zoomState.focalY * scale;
      this.zoomState.moved = true;
      this.clampZoomPan();
      this.updateZoomTransform();
      return;
    }

    if (!this.zoomState.active || event.touches.length !== 1) return;
    if (event.cancelable) event.preventDefault();

    const touch = event.touches[0];
    if (this.zoomState.mode !== 'pan') this.startPan(touch);

    const deltaX = touch.clientX - this.zoomState.startTouchX;
    const deltaY = touch.clientY - this.zoomState.startTouchY;
    this.zoomState.x = this.zoomState.startX + deltaX;
    this.zoomState.y = this.zoomState.startY + deltaY;
    this.zoomState.moved = this.zoomState.moved || Math.hypot(deltaX, deltaY) > TAP_MOVE_DISTANCE;
    this.clampZoomPan();
    this.updateZoomTransform();
  }

  onTouchEnd(event) {
    if (this.zoomState.mode === 'tap' && !this.zoomState.active) {
      if (event.touches.length > 0) return;

      const touch = event.changedTouches[0];
      const distance = touch
        ? Math.hypot(
            touch.clientX - this.zoomState.startTouchX,
            touch.clientY - this.zoomState.startTouchY
          )
        : Infinity;
      const image = this.zoomState.tapImage;

      this.zoomState.mode = null;
      this.zoomState.tapImage = null;

      if (image && distance <= TAP_MOVE_DISTANCE && this.handleTap(image, touch)) {
        if (event.cancelable) event.preventDefault();
      }
      return;
    }

    if (!this.zoomState.active) return;

    if (this.zoomState.mode === 'pinch') {
      if (event.touches.length === 1 && this.zoomState.scale > ZOOM_RESET_THRESHOLD) {
        this.startPan(event.touches[0]);
        return;
      }

      this.zoomState.mode = null;
      if (this.zoomState.scale <= ZOOM_RESET_THRESHOLD) this.resetImageZoom();
      return;
    }

    if (this.zoomState.mode !== 'pan' || event.touches.length > 0) return;

    const wasTap = !this.zoomState.moved;
    const source = this.zoomState.source;
    const touch = event.changedTouches[0];
    this.zoomState.mode = null;

    if (wasTap && source && this.handleTap(source, touch)) {
      if (event.cancelable) event.preventDefault();
    }
  }

  onTouchCancel() {
    this.zoomState.mode = null;
    if (this.zoomState.active && this.zoomState.scale <= ZOOM_RESET_THRESHOLD) {
      this.resetImageZoom();
    }
  }

  handleTap(image, touch) {
    if (!touch) return false;

    const tap = {
      image,
      time: Date.now(),
      x: touch.clientX,
      y: touch.clientY,
    };
    const isDoubleTap =
      this.lastTap?.image === image &&
      tap.time - this.lastTap.time <= DOUBLE_TAP_DELAY &&
      Math.hypot(tap.x - this.lastTap.x, tap.y - this.lastTap.y) <= DOUBLE_TAP_DISTANCE;

    if (!isDoubleTap) {
      this.lastTap = tap;
      return false;
    }

    this.lastTap = null;
    this.ignoreDblClickUntil = Date.now() + DOUBLE_TAP_DELAY;

    if (this.zoomState.active) {
      this.resetImageZoom();
    } else {
      this.activateImageZoom(image, DOUBLE_TAP_ZOOM, tap.x, tap.y);
    }

    return true;
  }

  onDoubleClick(event) {
    if (!this.isOpen) return;

    if (Date.now() <= this.ignoreDblClickUntil) {
      event.preventDefault();
      return;
    }

    const source = this.zoomState.active ? this.zoomState.source : this.getStackImage(event.target);
    if (!source) return;

    event.preventDefault();

    if (this.zoomState.active) {
      this.resetImageZoom();
    } else {
      this.activateImageZoom(source, DOUBLE_TAP_ZOOM, event.clientX, event.clientY);
    }
  }

  openGallery(slides, selectedIndex) {
    if (!this.pswpElement || !this.stackElement || !this.scrollWrap || this.isOpen) return;

    if (!Array.isArray(slides) || !slides.length) slides = this.getGallerySlides();
    if (!slides.length) return;

    const index = Number.isNaN(selectedIndex) ? 0 : selectedIndex;

    this.resetImageZoom();
    this.stackElement.replaceChildren(...slides.map((slide) => this.buildStackItem(slide)));

    this.isOpen = true;
    this.pswpElement.classList.add('pswp--open', 'pswp--stack');
    this.pswpElement.setAttribute('aria-hidden', 'false');

    disableBodyScroll(this.scrollWrap, bodyScrollLockOptions);
    document.addEventListener('keydown', this.onKeydown);
    document.documentElement.classList.add('pswp-gallery-open');

    requestAnimationFrame(() => {
      const target = this.stackElement.children[index] ?? this.stackElement.children[0];
      target?.scrollIntoView({ block: 'start' });
    });
  }

  onKeydown(event) {
    if (event.key === 'Escape') this.closeGallery();
  }

  closeGallery() {
    if (!this.isOpen || !this.pswpElement) return;

    this.isOpen = false;
    this.resetImageZoom();
    enableBodyScroll(this.scrollWrap);
    document.removeEventListener('keydown', this.onKeydown);

    this.pswpElement.classList.remove('pswp--open', 'pswp--stack');
    this.pswpElement.setAttribute('aria-hidden', 'true');
    this.stackElement.replaceChildren();
    document.documentElement.classList.remove('pswp-gallery-open');
  }
}

window.RevolutionImageZoom = RevolutionImageZoom;
